import { z } from "zod/v4";
import { zql } from "../../zero/schema";
import { Transaction, type QueryRowType } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import {
  SI_PREFIX_MULTIPLIERS,
  UNIT_DEFINITIONS,
  type UnitDefinition,
  type UnitType,
} from "./definitions";
import { must, safeParseFloat } from "@corpuscomplete/core/utils";

/**
 * Unit namespace contract for key validation and quantity conversion.
 *
 * Invariants:
 * - Unit keys derive from `UNIT_DEFINITIONS` and SI-prefix expansion only.
 * - Conversion is valid only between units that share the same base unit type.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace Unit {
  export const all = defineQuery(z.undefined(), () => zql.unit);
  export type Info = QueryRowType<typeof all>;

  export function byType(tx: Transaction, types: UnitType[]) {
    return tx.run(zql.unit.where("type", "IN", types));
  }

  export type Type = UnitType;
  type Definitions = typeof UNIT_DEFINITIONS;
  export type Key<T extends Type = Type> = {
    [K in keyof Definitions]: T extends Definitions[K]["baseUnit"]["type"]
      ?
          | `${K}`
          | (Definitions[K] extends {
              siPrefixes: (infer P extends string)[];
            }
              ? `${P}${K}`
              : never)
      : never;
  }[keyof Definitions];

  type EnumHelper<T extends string> = {
    [K in T]: K;
  };

  export const keySchema = <T extends Type = Type>(
    typeOrTypes?: T | T[],
  ): z.ZodEnum<EnumHelper<Key<T>>> => {
    const types: string[] | null = !typeOrTypes
      ? null
      : Array.isArray(typeOrTypes)
        ? typeOrTypes
        : [typeOrTypes];
    const keys: Key<T>[] = [];
    for (const key in UNIT_DEFINITIONS) {
      const definition = UNIT_DEFINITIONS[key as keyof Definitions];
      if (!types || types.includes(definition.baseUnit.type)) {
        keys.push(key as Key<T>);
        if ("siPrefixes" in definition) {
          for (const prefix of definition.siPrefixes) {
            keys.push((prefix + key) as Key<T>);
          }
        }
      }
    }
    return z.enum(keys);
  };

  export type Quantity<T extends Type = Type, K extends Key<T> = Key<T>> = {
    quantity: number;
    unitKey: K;
  };
  export const inputAsQuantity = <T extends Type = Type, K extends Key<T> = Key<T>>(
    input: string,
    unitKey: K,
  ): Quantity<T, K> | null => {
    const quantity = safeParseFloat(input);
    if (!quantity) {
      return null;
    }
    return { quantity, unitKey };
  };

  const flattenUnitDefinitions = (): Record<string, UnitDefinition> => {
    const result = {} as Record<string, UnitDefinition>;
    for (const key in UNIT_DEFINITIONS) {
      const definition = UNIT_DEFINITIONS[key as keyof typeof UNIT_DEFINITIONS];
      if (key in result) {
        throw new Error(`Duplicate unit definition: ${key}`);
      }
      result[key] = definition;

      const siPrefixes = "siPrefixes" in definition ? definition.siPrefixes : [];
      for (const siPrefix of siPrefixes ?? []) {
        const siKey = `${siPrefix}${key}`;
        if (siKey in result) {
          throw new Error(`Duplicate unit definition: ${siKey}`);
        }
        result[siKey] = {
          baseUnit: definition.baseUnit,
          multiplier: definition.multiplier * SI_PREFIX_MULTIPLIERS[siPrefix],
        };
      }
    }
    return result;
  };
  const flatUnitDefinitions = flattenUnitDefinitions();

  export const convert = <T extends Type = Type, K extends Key<T> = Key<T>>(
    quantity: Unit.Quantity,
    toUnitKey: K,
  ): Quantity<T, K> => {
    return must(
      safeConvert(quantity, toUnitKey),
      `Could not convert from unit "${quantity.unitKey}" to "${toUnitKey}"`,
    );
  };

  export const safeConvert = <T extends Type = Type, K extends Key<T> = Key<T>>(
    unitQuantity: Unit.Quantity,
    toUnitKey: K,
  ): Quantity<T, K> | null => {
    if (unitQuantity.unitKey === toUnitKey) {
      return unitQuantity as Quantity<T, K>;
    }
    const fromDefinition = flatUnitDefinitions[unitQuantity.unitKey];
    const toDefinition = flatUnitDefinitions[toUnitKey];
    if (!fromDefinition || !toDefinition) {
      return null;
    }
    if (fromDefinition.baseUnit.type !== toDefinition.baseUnit.type) {
      return null;
    }

    const factor = fromDefinition.multiplier / toDefinition.multiplier;
    return {
      quantity: unitQuantity.quantity * factor,
      unitKey: toUnitKey,
    };
  };
}
